import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpDown, LocateFixed } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "@/components/Reveal";
import { AddressInput } from "@/components/AddressInput";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-[#FAF7F2] border border-[#E5DEC9] focus:border-[#C5A059] outline-none px-4 py-3.5 text-sm font-medium text-[#111318] placeholder:text-[#6B7280] transition-colors duration-300";

export const TIME_SLOTS = Array.from({ length: 38 }, (_, i) => {
  const mins = 5 * 60 + i * 30;
  const h24 = Math.floor(mins / 60);
  const mm = String(mins % 60).padStart(2, "0");
  const h12 = ((h24 + 11) % 12) + 1;
  return { value: `${String(h24).padStart(2, "0")}:${mm}`, label: `${h12}:${mm} ${h24 >= 12 ? "PM" : "AM"}` };
});

export function QuoteWidget() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("transfer");
  const [locating, setLocating] = useState(false);
  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser can't share location — please type the address.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.get("/geo/reverse", { params: { lat: pos.coords.latitude, lng: pos.coords.longitude } });
          setForm((f) => ({ ...f, pickup: data.label }));
          toast.success("Location found — pickup address filled in.");
        } catch (err) {
          toast.error(formatApiError(err));
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocating(false);
        toast.error("Location unavailable — allow location access, or type the address.");
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  };
  const [form, setForm] = useState({ pickup: "", dropoff: "", date: "", time: "", hours: 2 });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const go = (e) => {
    e.preventDefault();
    const params = new URLSearchParams({ mode, pickup: form.pickup, date: form.date, time: form.time });
    if (mode === "transfer") params.set("dropoff", form.dropoff);
    else params.set("hours", form.hours);
    navigate(`/book?${params.toString()}`);
  };

  return (
    <section id="contact" className="relative py-28 lg:py-36 bg-[#F4EFE6] border-y border-[#E5DEC9]" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
        <Reveal>
          <Overline className="text-amber-700 font-semibold">Reserve</Overline>
          <h2 className="font-serif-lux font-normal text-4xl sm:text-5xl lg:text-6xl text-[#111318] mt-4 leading-tight">
            Your chauffeur, <span className="gold-text italic">one tap away.</span>
          </h2>
          <p className="mt-6 text-sm font-medium text-[#3A4150] leading-relaxed max-w-md">
            Transfer pricing scales with distance on the real driving route — $10 per mile for short hops
            ($50 minimum), $7 to 20 miles, $6 to 30, and $5 per mile beyond. By the hour, the Range Rover
            Autobiography is yours from $100/hour on longer charters. Every fare confirmed by our concierge
            before you ride.
          </p>
          <div className="mt-10 space-y-4">
            <div className="hairline" />
            <p className="font-mono-lux text-[11px] font-semibold uppercase tracking-[0.3em] text-[#3A4150]">
              Concierge Line <span className="text-amber-700 ml-3">+1 (786) 874-0320</span>
            </p>
            <p className="font-mono-lux text-[11px] font-semibold uppercase tracking-[0.3em] text-[#3A4150]">
              Correspondence <span className="text-amber-700 ml-3">concierge@aureusluxury.com</span>
            </p>
            <p className="font-mono-lux text-[11px] font-semibold uppercase tracking-[0.3em] text-[#3A4150]">
              Based In <span className="text-amber-700 ml-3">Palm Beach, South Florida</span>
            </p>
            <div className="hairline" />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={go} className="bg-white border border-[#E5DEC9] shadow-lg p-6 sm:p-8" data-testid="quote-widget">
            <div className="grid grid-cols-2 border border-[#E5DEC9] mb-6" data-testid="quote-mode-tabs">
              {[
                { id: "transfer", label: "PTP/Transfer", sub: "from $5 / mile" },
                { id: "hourly", label: "By the Hour", sub: "from $100 / hr" },
              ].map((m) => (
                <button
                  type="button"
                  key={m.id}
                  onClick={() => setMode(m.id)}
                  data-testid={`quote-mode-${m.id}`}
                  className={`py-4 text-center transition-colors duration-300 ${
                    mode === m.id ? "bg-[#16181D] text-amber-50" : "bg-transparent text-[#3A4150] hover:bg-[#FAF7F2]"
                  }`}
                >
                  <span className="block font-serif-lux text-xl font-medium">{m.label}</span>
                  <span className="block font-mono-lux text-[10px] font-semibold uppercase tracking-[0.2em] mt-1 opacity-80">{m.sub}</span>
                </button>
              ))}
            </div>

            <div className="space-y-4">
              <AddressInput
                value={form.pickup}
                onChange={(v) => setForm((f) => ({ ...f, pickup: v }))}
                placeholder="Pickup — e.g. Palm Beach Island"
                className={inputCls}
                testId="quote-pickup-input"
              />
              <div className="flex justify-end -mt-2">
                <button
                  type="button"
                  onClick={useCurrentLocation}
                  disabled={locating}
                  data-testid="quote-use-location"
                  className="inline-flex items-center gap-1.5 font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-[#6B7280] hover:text-amber-700 transition-colors duration-300 disabled:opacity-50"
                >
                  <LocateFixed size={12} strokeWidth={1.5} /> {locating ? "Locating…" : "Use current location"}
                </button>
              </div>
              {mode === "transfer" && (form.pickup || form.dropoff) && (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, pickup: f.dropoff, dropoff: f.pickup }))}
                    data-testid="quote-swap-addresses"
                    className="inline-flex items-center gap-2 font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-[#6B7280] hover:text-amber-700 transition-colors duration-300"
                  >
                    <ArrowUpDown size={12} strokeWidth={1.5} /> Swap addresses
                  </button>
                </div>
              )}
              {mode === "transfer" ? (
                <AddressInput
                  value={form.dropoff}
                  onChange={(v) => setForm((f) => ({ ...f, dropoff: v }))}
                  placeholder="Destination — e.g. Miami Intl (MIA)"
                  className={inputCls}
                  testId="quote-dropoff-input"
                />
              ) : (
                <label className="block">
                  <span className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.25em] text-[#3A4150] mb-2 block">Hours</span>
                  <input
                    type="number"
                    min={2}
                    max={12}
                    value={form.hours}
                    onChange={set("hours")}
                    className={inputCls}
                    data-testid="quote-hours-input"
                  />
                </label>
              )}
              <div className="grid grid-cols-2 gap-4">
                <input type="date" required value={form.date} onChange={set("date")} className={inputCls} data-testid="quote-date-input" />
                <select required value={form.time} onChange={set("time")} className={inputCls} data-testid="quote-time-input">
                  <option value="" disabled>Time</option>
                  {TIME_SLOTS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              type="submit"
              data-testid="quote-submit-button"
              className="group mt-6 w-full flex items-center justify-center gap-3 font-mono-lux text-[11px] font-bold uppercase tracking-[0.3em] px-10 py-4 bg-amber-500 text-black hover:bg-amber-600 transition-colors duration-300"
            >
              See Your Fare
              <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="mt-4 text-center text-[11px] font-medium text-[#6B7280]">
              Range Rover Autobiography · Chauffeur included
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
